import styled from "styled-components";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import React from "react";
import Divider from "~/components/traffic-tickets/steps/Divider";
import Tooltip from "~/components/Tooltip";
import CommunicationsToggle from "~/components/parking/Details/CommunicationsToggle";
import { PAYMENT_PROVIDERS } from "~/utilities/constants";

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 340px;
  flex-shrink: 1;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 9px 24px rgba(0, 0, 0, 0.1);
  ${props => props.theme.media.smallDesktop`
    margin-top: 25px;
  `}
  ${props => props.theme.media.mobile`
    width: 100%;
  `}
`

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const ItemName = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-family: ${props => props.theme.fonts.avenirNextRegular};
  color: ${props => props.theme.colors.text.label};
  font-size: 1em;
`

// Total row label keeps the original size/weight so the total stays
// prominent while the fee line items above are de-emphasized.
const TotalName = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-family: ${props => props.theme.fonts.avenirNextMedium};
  color: ${props => props.theme.colors.text.label};
  font-size: 1.125em;
`

const LabelWithTooltip = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
`
const ItemAmount = styled.div`
  font-family: ${props => props.theme.fonts.avenirNextDemi};
  color: ${props => props.theme.colors.text.label};
  font-size: 1em;
`

const TotalAmount = styled.div`
  font-family: ${props => props.theme.fonts.avenirNextDemi};
  color: ${props => props.theme.colors.success};
  font-size: 1.7em;
`

const AmountBreakdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 1em;
  padding-top: 1.5em;
`

const CommonOrderComponent = ({ payableAmount, serviceFee, processingFee, userCredits, paySubTotal, paymentProcessor }) => (
  <CardContainer>
    <AmountBreakdownContainer>
      <InfoRow>
        <ItemName>Fine Amount</ItemName>
        <ItemAmount>${payableAmount.toFixed(2)}</ItemAmount>
      </InfoRow>
      <Divider top={16} bottom={16} />
      <InfoRow>
        <ItemName>
          <LabelWithTooltip>
            {paymentProcessor === PAYMENT_PROVIDERS.evervault ? "NYC Credit Card Fee" : "Service Fee"}
            {paymentProcessor === PAYMENT_PROVIDERS.evervault ? (
              <Tooltip
                message="This fee is charged directly by (and paid to) the government agency for the use of a credit card to make your payment."
                icon="info"
                size={16}
                margin="0 0 0 6px"
              />
            ) : null}
          </LabelWithTooltip>
        </ItemName>
        <ItemAmount>${serviceFee.toFixed(2)}</ItemAmount>
      </InfoRow>
      <Divider top={16} bottom={16} />
      <InfoRow>
        <ItemName>
          <LabelWithTooltip>
            Processing Fee
            <Tooltip
              message="This is our fee for the ticket processing services we provide"
              icon="info"
              size={16}
              margin="0 0 0 6px"
            />
          </LabelWithTooltip>
        </ItemName>
        <ItemAmount>${processingFee.toFixed(2)}</ItemAmount>
      </InfoRow>
      { userCredits ? (
        <>
          <Divider top={16} bottom={16} />
          <InfoRow>
            <ItemName>
              Credits
              <Tooltip
                message={paymentProcessor === PAYMENT_PROVIDERS.evervault ? "There are credits on your account! They will be deducted from the processing fee." : "There are credits on your account! They will be deducted from the final price."}
                icon="info"
                size={16}
              />
            </ItemName>
            <ItemAmount>- ${userCredits.toFixed(2)}</ItemAmount>
          </InfoRow>
        </>
      ) : null}
      <Divider top={16} bottom={12} />
      <InfoRow>
        <TotalName>Total</TotalName>
        <TotalAmount>${paySubTotal.toFixed(2)}</TotalAmount>
      </InfoRow>
    </AmountBreakdownContainer>
    <CommunicationsToggle />
  </CardContainer>
)

CommonOrderComponent.propTypes = {
  payableAmount: PropTypes.number.isRequired,
  serviceFee: PropTypes.number.isRequired,
  processingFee: PropTypes.number.isRequired,
  userCredits: PropTypes.number.isRequired,
  paySubTotal: PropTypes.number.isRequired
}

export default observer(CommonOrderComponent)
